import axios, { AxiosInstance } from 'axios';
import { EditorTree } from '../../../types/types';

/**
 * Real API implementation for computation service
 */
class RealComputationService {
  private api: AxiosInstance;
private getToken: () => string | null;

  constructor(baseURL: string, getToken: () => string | null) {
    this.getToken = getToken;
    this.api = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    this.api.interceptors.request.use(config => {
      const token = this.getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Calculate tree values on the backend
   * Connects to the /trees/{id}/evaluate endpoint
   */
  async calculateTree(tree: EditorTree): Promise<EditorTree> {
    const treeId = tree.id;
    
    // Send the tree data for evaluation
    const response = await this.api.post(`/trees/${treeId}/evaluate`, {
      settings: {
        risk_neutral: true, // Default risk setting
        solve_method: "max" // Default solution method
      }
    });
    
    // Update tree with calculated values
    const result = response.data;
    
    // Merge evaluation results into the tree
    return {
      ...tree,
      total_expected_value: result.evaluation.expected_value || 0,
      updatedAt: new Date().toISOString(),
      // Add any other evaluation properties that might be needed
    };
  }

  /**
   * Run sensitivity analysis for a tree
   */
  async runSensitivityAnalysis(treeId: string, params: any): Promise<any> {
    const response = await this.api.post(`/trees/${treeId}/sensitivity`, params);
    return response.data;
  }
  
  /**
   * Run Monte Carlo simulation for a tree
   */
  async runSimulation(treeId: string, settings: any): Promise<any> {
    const response = await this.api.post(`/trees/${treeId}/simulate`, { settings });
    return response.data;
  }

  /**
   * Generate the influence diagram information for a tree
   */
  async generateInfluenceDiagram(treeId: string): Promise<any> {
    const response = await this.api.post(`/trees/${treeId}/influence`);
    return response.data;
  }

  async generateAISummary(params: any) {
        const response = await this.api.post(`/summarize/${params.id}`, {
            tree: params.tree_data || {},
            tree_config: params.tree_config || {},
            chat_history: params.chat_history || [],
            user_prompt: params.user_prompt || " ",
        });

        return {
            id: `ai_summary_${Date.now()}`,
            type: "ai-summary",
            data: response.data,
            createdAt: new Date().toISOString(),
            ok: response.status >= 200 && response.status < 300,
        };
    }
  }

export default RealComputationService;
